import { Component, inject, Injectable } from '@angular/core';
import { CodeSnippet } from './code-snippet.interface';
import { Observable, Subject, lastValueFrom } from 'rxjs';
import { DataPointsService } from '../../datapoints-module';

@Component({
    selector: 'add-datapoint-snippet',
    template: `
        <clx-popover
            #editorwindow
            *ngIf="isDialogOpen"
            [minWidth]="300"
            [maxWidth]="800"
            class="popup"
            height="auto"
            width="auto"
            title="Choose Datapoint"
            i18n-title="@@script-editor.snippet.add-datapoint.popup.title"
            [(visible)]="isDialogOpen"
            (visibleChange)="visibilityChanged($event)"
        >
            <div
                class="error message"
                *ngIf="error"
                i18n="@@script-editor.snippet.popup.message.plant-missing"
            >Please choose a plant/system before adding a datapoint</div>
            <clx-row
                has="full-size-columns big-layout">
                <clx-item>
                <clx-label>Variable name (OOP)</clx-label>
                <clx-checkbox name="OOP mode"
                    #oopMode
                    [(value)]="useOOPMode"
                    selected="true"></clx-checkbox>
                    <clx-input
                        name="varname"
                        #varname
                        value="plant"
                        [disabled]="oopMode.value ? null : true"
                    >
                        <clx-validation type="required"></clx-validation>
                    </clx-input>

                </clx-item>
            </clx-row>

            <clx-datapoint-selection
                *ngIf="!error && plantId"
                [plantId]="plantId"
                [selected]="plantId"
                show="viewNode"
                mode="viewNode"
                name="selectDatapoint"
                (selection)="selectDatapoint($event, {oopMode: oopMode.value, varname: varname.value, cmp: oopMode })"
            ></clx-datapoint-selection>

            <clx-datapoint-selection
                *ngIf="!error && systemId"
                [systemId]="systemId"
                [selected]="systemId"
                show="viewNode"
                mode="systemPlant"
                name="selectDatapoint2"
                (selection)="selectDatapoint($event, {oopMode: oopMode.value, varname: varname.value, cmp: oopMode })"
            ></clx-datapoint-selection>
        </clx-popover>
    `,
    standalone: false
})

@Injectable()
export class AddDatapointSnippet implements CodeSnippet{

    datapointService: DataPointsService = inject(DataPointsService);


    part = true;

    text: string = $localize`:@@script-editor.snippet.datapoint-id:DatapointId`;

    static category: string = 'climatix';

    isDialogOpen: boolean = false;

    createDialog: boolean = false;

    subject: Subject<any> = new Subject()

    observable: Observable<any> = this.subject.asObservable()

    useOOPMode: boolean = false;


    async selectDatapoint(datapoint, extravalue) {

        let dp2 = { ...datapoint };

        if (datapoint.datapointId != null) {
            let dpinfo = await this.datapointService.getDataPointDetails(datapoint.plantId + ";" + datapoint.datapointId);
            dp2.dpname = dpinfo.objectName;
        }

        dp2.data = extravalue ?? {};
        dp2.dpd = this.parseDatapoint(datapoint.datapointId);
        this.subject.next(dp2);
        this.subject.complete();
    }

    public isPlantSelectionOpen: boolean = false;

    public choosePlant() {
        this.isPlantSelectionOpen = true;
    }

    public selectPlant(data) {
        if (data.systemId) {
            this.systemId = data.systemId;
        }

        this.plantId = data.plantId;
    }


    visibilityChanged(value) {
        if(value === false) {
            this.subject.next(null);
            this.isDialogOpen = false;
            this.subject.complete();
        }
    }

    public parseDatapoint(datapoint: string) {

        let rxOuter = new RegExp("^([0-1])!(.*)$");

        let mo = rxOuter.exec(datapoint);
        if (mo == null)
            return null;

        let mode = mo[1];
        let dp = mo[2];
        let alias = "";

        switch (mode) {
            case '0': alias = "system"; break;
            case '1': alias = "app"; break;
            default:
        }

        if (alias == "")
            return null;

        let rxInner = new RegExp("^[a-zA-Z_][a-zA-Z0-9_]*$");

        let mi = rxInner.exec(dp);

        let result = {
            needsQuotation: !mi,
            subObject: alias,
            dp: dp,
            path: ""
        };

        if (result.needsQuotation) {
            result.path = `.${alias}['${dp}']`;
        } else {
            result.path = `.${alias}.${dp}`;
        }

        return result;
    }

    error: boolean = false;

    data: any;

    plantId: string;

    systemId: string;

    async insert(): Promise<string> {

        this.useOOPMode = this.data.oop;

        this.plantId = null;
        this.systemId = null;
        this.error = true;

        if (this.data?.plantInput?.value) {
            this.plantId = this.data.plantInput.value;
        }else if (this.data?.systemInput?.value) {
            this.systemId = this.data.systemInput.value;
        } else {
            return '';
        }

        this.error = false;

        this.subject = new Subject();
        this.observable = this.subject.asObservable();
        this.isDialogOpen = true;
        const value = await lastValueFrom(this.observable);
        this.isDialogOpen = false;
        if (!value)
            return;

        if (value.data.oopMode) {
            return `${value.data.varname}${value.dpd.path}`;
        } else {
            return value ? value.datapointId : ''
        }
    }

}